import React, { useState } from 'react';
import type { GameConfig } from '../types/dualGame';

interface ConfigMenuProps {
  defaultConfig: GameConfig;
  onStart: (config: GameConfig) => void;
}

const PRESETS = {
  quick:    { duration: 60,  targetScore: 4096,  boardSize: 4, label: '快速对战', desc: '1分钟 / 4096分' },
  standard: { duration: 180, targetScore: 8192,  boardSize: 4, label: '标准对战', desc: '3分钟 / 8192分' },
  epic:     { duration: 300, targetScore: 16384, boardSize: 4, label: '史诗对战', desc: '5分钟 / 16384分' },
} as const;

type PresetKey = keyof typeof PRESETS;

export const ConfigMenu: React.FC<ConfigMenuProps> = ({ defaultConfig, onStart }) => {
  const [config, setConfig] = useState<GameConfig>(defaultConfig);
  const [activePreset, setActivePreset] = useState<PresetKey | null>('standard');

  const applyPreset = (key: PresetKey) => {
    const p = PRESETS[key];
    setConfig({ duration: p.duration, targetScore: p.targetScore, boardSize: p.boardSize });
    setActivePreset(key);
  };

  const handleChange = (field: keyof GameConfig, value: number) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setActivePreset(null);
  };

  return (
    <div className="config-menu">
      <h2>游戏设置</h2>

      <div className="preset-buttons">
        {(Object.entries(PRESETS) as [PresetKey, typeof PRESETS[PresetKey]][]).map(([key, p]) => (
          <button
            key={key}
            className={`preset-btn ${activePreset === key ? 'active' : ''}`}
            onClick={() => applyPreset(key)}
          >
            {p.label}
            <span>{p.desc}</span>
          </button>
        ))}
      </div>

      <div className="config-divider">或自定义设置</div>

      <div className="config-options">
        <div className="config-item">
          <label htmlFor="duration">对战时长</label>
          <select id="duration" value={config.duration} onChange={e => handleChange('duration', +e.target.value)}>
            <option value={60}>1 分钟</option>
            <option value={120}>2 分钟</option>
            <option value={180}>3 分钟</option>
            <option value={300}>5 分钟</option>
            <option value={600}>10 分钟</option>
          </select>
        </div>

        <div className="config-item">
          <label htmlFor="targetScore">提前获胜目标</label>
          <select id="targetScore" value={config.targetScore} onChange={e => handleChange('targetScore', +e.target.value)}>
            <option value={0}>关闭提前获胜</option>
            <option value={2048}>2048 分</option>
            <option value={4096}>4096 分</option>
            <option value={8192}>8192 分</option>
            <option value={16384}>16384 分</option>
          </select>
        </div>

        <div className="config-item">
          <label htmlFor="boardSize">棋盘大小</label>
          <select id="boardSize" value={config.boardSize} onChange={e => handleChange('boardSize', +e.target.value)}>
            <option value={4}>4 × 4（标准）</option>
            <option value={5}>5 × 5（扩展）</option>
          </select>
        </div>
      </div>

      <button className="start-button" onClick={() => onStart(config)}>
        开始对战
      </button>
    </div>
  );
};

export default ConfigMenu;
